import { useEffect } from "react";
import { usePortfolioRebalanceStore } from "../../Store/portfolioRebalanceStore";
import { useAuth } from "../../Context/AuthProvider";

/**
 * Custom hook to handle WebSocket updates for portfolio rebalance operations
 * Listens for events related to account operations that affect portfolio rebalance
 */
export const usePortfolioRebalanceWebSocketUpdates = () => {
  const {
    accounts,
    selectedAccount,
    setSelectedAccount,
    fetchAccountPositions,
    positions,
    accountSummary,
    fetchAccounts,
  } = usePortfolioRebalanceStore();

  const { token, userId } = useAuth();

  useEffect(() => {
    // Notify WebSocket layer of active account change for portfolio rebalance
    if (selectedAccount) {
      window.selectedAccountId = selectedAccount;
      localStorage.setItem("selectedAccountId", selectedAccount);
      window.dispatchEvent(
        new CustomEvent("activeAccountChanged", { detail: selectedAccount })
      );
      console.log(
        `[portfolio rebalance ws] Active account changed: ${selectedAccount}`
      );
    }
  }, [selectedAccount]);

  useEffect(() => {
    // Handler for account update events (creation, modification, and deletion if isActive === 0)
    const handleAccountUpdate = (event) => {
      const updatedAccount = event.detail;
      console.log(
        "[portfolio rebalance ws] Received accountUpdate event:",
        updatedAccount
      );

      const accountIdentifier =
        updatedAccount.accountId || updatedAccount.accountMstId;

      if (!updatedAccount || !accountIdentifier) {
        console.log(
          "[portfolio rebalance ws] Invalid account update data received:",
          updatedAccount
        );
        return;
      }

      // If isActive is 0, treat as deletion
      if (updatedAccount.isActive === 0) {
        console.log(
          `[portfolio rebalance ws] Account ${accountIdentifier} marked as deleted (isActive=0)`
        );

        // Update state to remove the deleted account
        const remainingAccounts = accounts.filter(
          (acc) => acc.id !== accountIdentifier
        );
        usePortfolioRebalanceStore.setState((state) => ({
          accounts: remainingAccounts,
        }));

        // Handle account switching if the deleted account was currently selected
        if (selectedAccount === accountIdentifier) {
          if (remainingAccounts.length > 0) {
            const deletedIndex = accounts.findIndex(
              (acc) => acc.id === accountIdentifier
            );
            const nextAccountIndex =
              deletedIndex < remainingAccounts.length
                ? deletedIndex
                : remainingAccounts.length - 1;
            const nextAccount = remainingAccounts[nextAccountIndex];

            setSelectedAccount(nextAccount.id);
            console.log(
              `[portfolio rebalance ws] Switched to next available account after deletion: ${nextAccount.id}`
            );

            if (token) {
              fetchAccountPositions(nextAccount.id, token);
              console.log(
                `[portfolio rebalance ws] Fetched positions for new selected account: ${nextAccount.id}`
              );
            }
          } else {
            // No accounts remaining, clear selected account and data
            setSelectedAccount(null);
            console.log(
              "[portfolio rebalance ws] No accounts remaining after deletion, cleared selection and positions"
            );

            usePortfolioRebalanceStore.setState({
              positions: [],
              accountSummary: {
                totalPar: null,
                averageYieldToWorst: null,
                average: null,
                interest: null,
                averageSPRating: null,
                taxable: null,
                totalCurrValue: null,
                avgYieldToMaturity: null,
                convexity: null,
                next12MonthInterest: null,
                next12MonthPrincipal: null,
                totalDv01: null,
                averageMoodys: null,
                nonTaxable: null,
                accountMstId: null,
                accountIdentifier: null,
                accountDescription: null,
                custodianName: null,
                userId: null,
                // pmId: null,
                // pmName: null,
                userName: null,
              },
            });
          }
        }

        // Show modal if the deleted account is the currently viewed account
        const isViewingDeletedAccount = selectedAccount === accountIdentifier;
        if (isViewingDeletedAccount) {
          const deletedAccountObj = accounts.find(
            (acc) => acc.id === accountIdentifier
          );
          console.log(
            "[portfolio rebalance ws] Showing delete modal for currently viewed account:",
            accountIdentifier
          );
          window.dispatchEvent(
            new CustomEvent("showAccountDeletedModal", {
              detail: {
                deletedAccount: deletedAccountObj || updatedAccount,
              },
            })
          );
        }

        return;
      }

      // Handle account creation/update
      const existingAccount = accounts.find(
        (acc) => acc.id === accountIdentifier
      );
      const isNewAccount = !existingAccount;

      const formattedAccount = {
        id: accountIdentifier,
        accountId: accountIdentifier,
        // pmId: updatedAccount.pmId,
        userId: userId || null,
        // pmName: updatedAccount.pmName,
        userName: updatedAccount.userName || null,
        description: updatedAccount.description,
        custodianName: updatedAccount.custodianName,
        custodianId: updatedAccount.custodianId,
        accountNumber: updatedAccount.accountIdentifier,
        accountIdentifier: updatedAccount.accountIdentifier,
        accountType: updatedAccount.accountType || "Bond",
        status: updatedAccount.status,
        isActive: updatedAccount.isActive === 1,
        entryBy: updatedAccount.entryBy || null,
      };

      console.log(
        "[portfolio rebalance ws] Formatted account for update:",
        formattedAccount
      );

      const currentSelectedAccount = selectedAccount;

      usePortfolioRebalanceStore.setState((state) => {
        if (isNewAccount) {
          console.log(
            "[portfolio rebalance ws] Adding new account to list:",
            formattedAccount.id
          );
          const wasEmpty = state.accounts.length === 0;

          if (wasEmpty) {
            console.log(
              "[portfolio rebalance ws] First account added, auto-selecting:",
              formattedAccount.id
            );
            setTimeout(() => {
              setSelectedAccount(formattedAccount.id);
              if (token) {
                fetchAccountPositions(formattedAccount.id, token);
                console.log(
                  "[portfolio rebalance ws] Fetched positions for new account:",
                  formattedAccount.id
                );
              }
            }, 100);
          }

          return {
            accounts: [...state.accounts, formattedAccount],
            selectedAccount: wasEmpty
              ? formattedAccount.id
              : currentSelectedAccount,
          };
        } else {
          console.log(
            "[portfolio rebalance ws] Updating existing account:",
            formattedAccount.id
          );
          return {
            accounts: state.accounts.map((acc) => {
              if (acc.id === formattedAccount.id) {
                return {
                  ...acc,
                  ...formattedAccount,
                };
              }
              return acc;
            }),
            selectedAccount: currentSelectedAccount,
          };
        }
      });

      // Refresh positions if the updated account is currently selected
      if (existingAccount && selectedAccount === accountIdentifier) {
        if (token) {
          fetchAccountPositions(accountIdentifier, token);
          console.log(
            "[portfolio rebalance ws] Refreshed positions for updated selected account:",
            accountIdentifier
          );
        }
      }
    };

    // Handler for account summary update events
    const handleAccountSummaryUpdate = (event) => {
      const summary = event.detail;
      if (!summary) return;

      console.log(
        "[portfolio rebalance ws] Received accountSummary event:",
        summary
      );

      const summaryAccountIdentifier =
        summary.accountId || summary.accountMstId;

      // Only update summary if the account is currently active
      if (summaryAccountIdentifier !== selectedAccount) {
        console.log(
          "[portfolio rebalance ws] Ignored accountSummary event (not for active account):",
          summaryAccountIdentifier
        );
        return;
      }

      const mappedSummary = {
        totalPar: summary.totalParValue ?? null,
        totalCurrValue: summary.totalCurrentValue ?? null,
        averageYieldToWorst: summary.averageYieldToWorst ?? null,
        avgYieldToMaturity: summary.averageYieldToMaturity ?? null,
        average: summary.averageDuration ?? null,
        convexity: summary.averageConvexity ?? null,
        interest: summary.interest ?? null,
        next12MonthPrincipal: summary.next12MonthPrincipal ?? null,
        next12MonthInterest: summary.next12MonthInterest ?? null,
        averageSPRating: summary.averageSPRating ?? null,
        averageMoodys: summary.averageMoodyRating ?? null,
        nonTaxable: summary.totalNonTaxable ?? null,
        taxable: summary.totalTaxable ?? null,
        totalDv01: summary.totalDv01 ?? null,
        // Keep account info fields
        accountMstId: summary.accountMstId || accountSummary.accountMstId,
        accountIdentifier:
          summary.accountIdentifier || accountSummary.accountIdentifier,
        accountDescription:
          summary.accountDescription || accountSummary.accountDescription,
        custodianName: summary.custodianName || accountSummary.custodianName,
        userName: summary.userName || accountSummary.userName,
        userId: summary.userId || accountSummary.userId,
      };

      console.log(
        "[portfolio rebalance ws] Updating account summary in store:",
        mappedSummary
      );

      usePortfolioRebalanceStore.setState((state) => ({
        accountSummary: {
          ...state.accountSummary,
          ...mappedSummary,
        },
      }));

      // NOTE: intentionally do NOT re-fetch positions here. A recalc PUT causes
      // the backend to emit an accountSummary event; refetching on that event
      // re-ran the recalc sweep and created a request storm (feedback loop).
      // The summary is applied above; individual rows arrive via positionUpdate.
    };

    // Handler for position update events
    const handlePositionUpdate = (event) => {
      const updatedPosition = event.detail;
      console.log(
        "[portfolio rebalance ws] Received positionUpdate event:",
        updatedPosition
      );

      if (
        !updatedPosition ||
        !updatedPosition.accountPositionMappingId ||
        !updatedPosition.accountMstId
      ) {
        console.log(
          "[portfolio rebalance ws] Invalid position update data received:",
          updatedPosition
        );
        return;
      }

      const positionAccountIdentifier =
        updatedPosition.accountMstId || updatedPosition.accountId;

      // Only update if the updated position belongs to the currently selected account
      if (selectedAccount === positionAccountIdentifier) {
        // [updating account ws] Updating position in table for selected account
        console.log(
          `[updating account ws] Updating position for account ${selectedAccount}, CUSIP: ${updatedPosition.cusip}`
        );

        // Update the positions array in the store
        usePortfolioRebalanceStore.setState((state) => ({
          positions: state.positions.map((pos) =>
            pos.accountPositionMappingId ===
            updatedPosition.accountPositionMappingId
              ? {
                  ...pos,
                  ...updatedPosition,
                  securityWeightPercentage: updatedPosition.securityWeightPercentage ?? pos.securityWeightPercentage
                }
              : pos
          ),
        }));

        // Optionally, update the position in the accounts array as well
        usePortfolioRebalanceStore.setState((state) => ({
          accounts: state.accounts.map((acc) => {
            if (
              acc.id === positionAccountIdentifier &&
              Array.isArray(acc.positions)
            ) {
              return {
                ...acc,
                positions: acc.positions.map((pos) =>
                  pos.accountPositionMappingId ===
                  updatedPosition.accountPositionMappingId
                    ? {
                        ...pos,
                        ...updatedPosition,
                        securityWeightPercentage: updatedPosition.securityWeightPercentage ?? pos.securityWeightPercentage
                      }
                    : pos
                ),
              };
            }
            return acc;
          }),
        }));
      } else {
        console.log(
          `[portfolio rebalance ws] Ignored position update for account ${positionAccountIdentifier} (not active)`
        );
      }
    };

    // Add event listeners
    window.addEventListener("accountUpdate", handleAccountUpdate);
    window.addEventListener("accountSummary", handleAccountSummaryUpdate);
    window.addEventListener("positionUpdate", handlePositionUpdate);

    // Cleanup function
    return () => {
      window.removeEventListener("accountUpdate", handleAccountUpdate);
      window.removeEventListener("accountSummary", handleAccountSummaryUpdate);
      window.removeEventListener("positionUpdate", handlePositionUpdate);
    };
  }, [
    accounts,
    selectedAccount,
    setSelectedAccount,
    token,
    fetchAccountPositions,
    positions,
    userId,
    accountSummary,
  ]);
};
